import { get, reduce, forEach } from 'lodash-es'
import { Schema, normalize, arrayOf } from 'normalizr'
import { receivePhotos, photoDeleteSuccess } from '@actions/photo_actions.js'
import { camelizeKeys } from '@utils/object.js'
import jsonApiNormalize from '@base/json-api-normalizer.js'
import { toastError } from '@actions/toast_actions.js'
import { loadUserPhotos } from './client_actions.js'
import Types from '../types.js'

const UPLOAD_ERROR_PREFIX = 'ERROR-UPL'

export function handleLoadUserPhotosSuccess(res, pageNumber) {
  return (dispatch, getState) => {
    const parsed = res.data
    const photo = new Schema('photos')

    let normalized
    let photos
    let photoIds = []
    if (parsed.hasOwnProperty('jsonapi')) {
      // response is jsonapi style. Use json-api-normalizer to normalize, also manually map the photo ids
      normalized = jsonApiNormalize(parsed, { camelizeKeys: false, camelizeTypeValues: false })
      photoIds = reduce(
        normalized.photos,
        function (arr, photo) {
          arr.push(photo.id)
          return arr
        },
        []
      )
      photos = normalized.photos
    } else {
      // response is rest style, use normalizr directly
      normalized = normalize(parsed, { photos: arrayOf(photo) })
      photoIds = normalized.result.photos
      photos = normalized.entities.photos
    }
    dispatch(receivePhotos(photos))

    const paginationMetadata = get(parsed, ['meta', 'pagination'])
    const { currentPage, limitValue, totalCount } = camelizeKeys(paginationMetadata)
    const payload = {
      pageNumber,
      ids: photoIds,
      pagination: { currentPage, limitValue, totalCount },
    }
    dispatch({
      type: Types.USER_PHOTOS_SUCCESS,
      payload,
    })
  }
}

export const handleLoadUserPhotosError = (err, pageNumber) => ({
  type: Types.USER_PHOTOS_FAILURE,
  payload: {
    pageNumber,
  },
})

export function handleUploadSuccess(res) {
  return (dispatch, getState) => {
    const parsedJSON = res.data
    const apiVersion = parsedJSON.hasOwnProperty('jsonapi') ? 2 : 1
    let photos
    if (apiVersion === 2) {
      // jsonapi style respons
      const responseData = parsedJSON

      // first deal with errors if we have them
      if (responseData.hasOwnProperty('errors')) {
        forEach(responseData.errors, error => {
          // error is a single photo's serialized ActiveModel::Errors object
          // TODO: This could be done a little better and perhaps gather all errors into one toast
          const baseError = get(error, 'base') // { base: 'some base error', property: 'some property error' }
          if (baseError) {
            dispatch(toastError(baseError))
          } else {
            dispatch(toastError(`Failed to upload`))
          }
        })
      }

      // then deal with photos
      const normalized = jsonApiNormalize(parsedJSON, { camelizeKeys: false })
      photos = normalized.photos || null
    } else {
      // rest style response
      photos = []
      forEach(parsedJSON.data, item => {
        // each photo could fail, or be success
        if (item.errors) {
          dispatch(toastError(`Failed to upload`))
        } else {
          photos.push(item)
        }
      })
    }

    if (photos) {
      dispatch(receivePhotos(photos))
      dispatch(uploadSuccess())
      // now reload the first photo page which should include the new image
      dispatch(loadUserPhotos(1, apiVersion))
    } else {
      dispatch(uploadSuccess()) // request has finished and been dealt with, even if no photos were returned
    }
  }
}

export function handleUploadError(err) {
  return (dispatch, getState) => {
    const response = err.response
    switch (response.status) {
      case 413:
        dispatch(uploadFail())
        dispatch(toastError('File was too big'))
        break

      default:
        dispatch(uploadFail())
        dispatch(toastError(`Error uploading: ${UPLOAD_ERROR_PREFIX}-${response.status}`))
    }
  }
}

function uploadSuccess() {
  return {
    type: Types.PHOTO_UPLOAD_SUCCESS,
  }
}

function uploadFail() {
  return {
    type: Types.PHOTO_UPLOAD_FAILURE,
  }
}

export const handlePhotoDeleteSuccess = (res, photoId) => {
  return (dispatch, getState) => {
    // global photo deletion
    dispatch(photoDeleteSuccess(photoId))
    // photo library specific action type
    dispatch({
      type: Types.PHOTO_DELETE_SUCCESS,
      payload: photoId,
    })
  }
}

export const handlePhotoDeleteError = (err, photoId) => ({
  type: Types.PHOTO_DELETE_FAILURE,
  payload: photoId,
})
